import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import api from './axiosInstance.js';
import abroadifyLogo from '../assets/img/logo-large.png';

const getPageWidth = (doc) => doc.internal.pageSize.getWidth();
const getPageHeight = (doc) => doc.internal.pageSize.getHeight();

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const addPdfHeader = async (doc) => {
    try {
        const img = await loadImage(abroadifyLogo);

        const maxWidth = 58;
        const maxHeight = 20;

        const imgRatio = img.width / img.height;
        let renderWidth = maxWidth;
        let renderHeight = renderWidth / imgRatio;

        if (renderHeight > maxHeight) {
            renderHeight = maxHeight;
            renderWidth = renderHeight * imgRatio;
        }

        const x = (getPageWidth(doc) - renderWidth) / 2;
        const y = 10;

        doc.addImage(abroadifyLogo, 'PNG', x, y, renderWidth, renderHeight);
    } catch (error) {
        console.warn('Logo could not be added to PDF:', error);
        doc.setFontSize(18);
        doc.text('ABROADIFY', getPageWidth(doc) / 2, 20, { align: 'center' });
    }

    doc.setDrawColor(192, 217, 238);
    doc.line(14, 32, getPageWidth(doc) - 14, 32);
};

const addPdfFooter = (doc) => {
    const issuingDate = new Date().toLocaleDateString();

    doc.setDrawColor(192, 217, 238);
    const pageHeight = getPageHeight(doc);
    const footerY = pageHeight - 10;
    const lineY = pageHeight - 17;
    const rightX = getPageWidth(doc) - 14;

    doc.line(14, lineY, rightX, lineY);

    doc.setFontSize(9);
    doc.text(`Issuing Date: ${issuingDate}`, 14, footerY);

    doc.textWithLink('app.abroadify.me', rightX, footerY, {
        align: 'right',
        url: 'https://app.abroadify.me',
    });

    doc.setTextColor(0, 0, 0);
};

const addHeaderAndFooterToAllPages = async (doc) => {
    const totalPages = doc.getNumberOfPages();

    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        await addPdfHeader(doc);
        addPdfFooter(doc);
    }
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
};

const addSectionTable = (doc, title, rows, startY) => {
    const filteredRows = rows.filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0;
        return String(value).trim() !== '';
    });

    if (!filteredRows.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 14, startY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
        startY: startY + 4,
        head: [['Field', 'Value']],
        body: filteredRows.map(([label, value]) => [label, formatValue(value)]),
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 127 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

const buildIndustriesValue = (industries = [], industriesData = []) => {
    if (!Array.isArray(industries) || !industries.length) return '';

    return industries
        .map((item, index) => {
            const selectedIndustry = industriesData.find(
                (ind) => String(ind.code) === String(item.industry)
            );

            const industryName = selectedIndustry?.name || item.industry || '';

            const subNames = Array.isArray(item.subIndustry)
                ? item.subIndustry
                    .map((code) => {
                        // ✅ same logic as UI

                        // 1. Try subIndustries
                        const sub = (selectedIndustry?.subIndustries || [])
                            .find((s) => String(s.code) === String(code));
                        if (sub) return sub.name;

                        // 2. Try subSubIndustries
                        const subSub = (selectedIndustry?.subIndustries || [])
                            .flatMap((s) => s.subSubIndustries || [])
                            .find((ss) => String(ss.code) === String(code));

                        if (subSub) return subSub.name;

                        return null;
                    })
                    .filter(Boolean)
                    .join(', ')
                : '';

            return [
                `#${index + 1}`,
                industryName ? `Industry: ${industryName}` : '',
                subNames ? `Sub Industry: ${subNames}` : '',
            ]
                .filter(Boolean)
                .join(' | ');
        })
        .join('\n');
};

const buildStudyFieldsValue = (studyFields = []) => {
    if (!Array.isArray(studyFields) || !studyFields.length) return '';

    return studyFields
        .map((item, index) => {
            return [
                `#${index + 1}`,
                item.studyField ? `Study Field: ${item.studyField}` : '',
                item.preferredStudyDirections
                    ? `Preferred Study Directions: ${item.preferredStudyDirections}`
                    : '',
                item.specialistAreas ? `Specialist Areas: ${item.specialistAreas}` : '',
            ]
                .filter(Boolean)
                .join(' | ');
        })
        .join('\n');
};

const buildNaceMaps = (naceList = []) => {
    const industryMap = new Map();
    const subIndustryMap = new Map();

    (naceList || []).forEach((industry) => {
        if (industry?.code) {
            industryMap.set(String(industry.code), industry.name || String(industry.code));
        }

        (industry?.subIndustries || []).forEach((sub) => {
            if (sub?.code) {
                subIndustryMap.set(String(sub.code), sub.name || String(sub.code));
            }

            (sub?.subIndustries || []).forEach((sub2) => {
                if (sub2?.code) {
                    subIndustryMap.set(String(sub2.code), sub2.name || String(sub2.code));
                }
            });
        });
    });

    return { industryMap, subIndustryMap };
};

const getMappedValue = (map, value) => {
    if (value === null || value === undefined || value === '') return '';
    return map.get(String(value)) || String(value);
};

const getMappedArrayValues = (map, values = []) => {
    if (!Array.isArray(values) || !values.length) return '';
    return values.map((value) => getMappedValue(map, value)).join(', ');
};

export const exportCompanyPdf = async (id) => {
    const [companyResponse, naceResponse] = await Promise.all([
        api.get(`/api/companies/${id}`),
        api.get('/api/nace'),
    ]);

    const companyData = companyResponse.data || {};
    const naceList = Array.isArray(naceResponse.data) ? naceResponse.data : [];
    const industriesData = Array.isArray(naceResponse.data) ? naceResponse.data : [];

    const doc = new jsPDF();
    let y = 42;

    doc.setFontSize(18);
    doc.text(companyData.title || 'Company Export', 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    y = addSectionTable(
        doc,
        'General',
        [
            ['Title', companyData.title],
            ['URL', companyData.url],
            ['Access Level', companyData.accessLevel],
            ['Activity Status', companyData.activityStatus],
            ['Account Approval Status', companyData.accountApprovalStatus],
            ['Best Place to Work Rating', companyData.bestPlacetoWorkRating],
            ['Best Place to Work Description', companyData.bestPlacetoWorkDescription],
            ['Created At', companyData.created_at],
            ['Updated At', companyData.updatedAt],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'About / Locations',
        [
            ['Address', companyData.address],
            ['ZIP', companyData.zip],
            ['City', companyData.city],
            ['Country', companyData.country],
            ['Other Information', companyData.otherInformation],
            ['Child URL', companyData.childUrl],
            ['Phone', companyData.phone],
            ['Latitude', companyData.latitude],
            ['Longitude', companyData.longitude],
            ['Address 2', companyData.address2],
            ['ZIP 2', companyData.zip2],
            ['City 2', companyData.city2],
            ['Country 2', companyData.country2],
            ['Other Information 2', companyData.otherInformation2],
            ['Child URL 2', companyData.childUrl2],
            ['Phone 2', companyData.phone2],
            ['Latitude 2', companyData.latitude2],
            ['Longitude 2', companyData.longitude2],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Legal',
        [
            ['Legal Name', companyData.legalName],
            ['VAT', companyData.vat],
            ['DUNS', companyData.duns],
            ['Global Ultimate', companyData.globalUltimate],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Size / Rating',
        [
            ['Global Size', companyData.globalSize],
            ['Local Size', companyData.localSize],
            ['Rating', companyData.rating],
            ['Rating Source', companyData.ratingSource],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Industries',
        [['Industries', buildIndustriesValue(companyData.industries, industriesData)]],
        y
    );

    y = addSectionTable(
        doc,
        'Business Type',
        [
            ['Business Purpose', companyData.businessPurpose],
            ['Local Activities', companyData.localActivities],
            ['Local Staffing', companyData.localStaffing],
            ['Business Type Other', companyData.businessTypeOther],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Fortune',
        [
            ['Is Fortune', companyData.isFortune],
            ['Fortune Rank', companyData.fortuneRank],
            ['Fortune Year', companyData.fortuneYear],
            ['Fortune Revenue', companyData.fortuneRevenue],
            ['Fortune % Change', companyData.fortunePercentChange],
            ['Fortune Profit', companyData.fortuneProfit],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'HR',
        [
            ['HR Full Name', companyData.hrFullName],
            ['HR Recruiting URL', companyData.hrRecrutingUrl],
            ['HR Email', companyData.hrEmail],
            ['HR Phone', companyData.hrPhone],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Jobs / Hiring',
        [
            ['Opened Student', companyData.openedStudent],
            ['Student Job Areas', companyData.studentJobAreas],
            ['Opened World Traveler', companyData.openedWorldTraveler],
            ['World Traveler Areas', companyData.worldTravelerAreas],
            ['Opened English Speaking', companyData.openedEnglishSpeaking],
            ['English Speaking Areas', companyData.enSpeakingAreas],
            ['Open Jobs Student', companyData.openJobsStudent],
            ['Open Jobs Intern', companyData.openJobsIntern],
            ['Open Jobs Starting', companyData.openJobsStarting],
            ['Open Jobs 1-3', companyData.openJobs13],
            ['Open Jobs 3+', companyData.openJobs3],
        ],
        y
    );

    y = addSectionTable(
        doc,
        'Study Fields',
        [['Study Fields', buildStudyFieldsValue(companyData.studyFields)]],
        y
    );

    await addHeaderAndFooterToAllPages(doc);

    const safeFileName = (companyData.title || 'company')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

    doc.save(`${safeFileName}-export.pdf`);
};